/* eslint-disable eqeqeq */
import React, { useCallback, useEffect, useState } from "react";
import MainBody from "../../components/MainBody/MainBody";
import { useLocation, useNavigate } from "react-router-dom";
import {
  API,
  defaultLimitPagination,
  fileUpload,
  TableClasses,
  TableWrapClasses,
  TDClasses,
  THClasses,
  TRClasses,
} from "../../config";
import moment from "moment";
import BootstrapTable from "react-bootstrap-table-next";
import Pagination from "../../components/Pagination";
import {
  formatAccountNumber,
  formatBSBNumber,
  formatContactName,
  formatNumber,
  NegativeAmountConverter,
  pastFinancialdate,
} from "../../lib/Misc";
import { Download, Home } from "lucide-react";
import { MonthRangePicker } from "../../components/ui/monthrangepicker";
import { useMediaQuery } from "../../components/use-media-query";
import CommonFilters from "../../components/CommonFilters";
import { useTrust } from "../../lib/context/TrustContext";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../../components/ui/accordion";
import { Card, CardContent } from "../../components/ui/card";
import { useOrganization } from "../../lib/context/OrganisationContext";
import DropDownButton from "../../components/DropDownButton";

const Cashbook = ({ org = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const query = new URLSearchParams(location.search);
  const defaultPage = query.get("page");
  const defaultLimit = query.get("limit");
  const fromMonth = query.get("from")
    ? new Date(
        `${query.get("from")?.split("-")[1]}-${
          query.get("from")?.split("-")[0]
        }-01`
      )
    : "";
  const toMonth = query.get("to")
    ? new Date(
        `${query.get("to")?.split("-")[1]}-${query.get("to")?.split("-")[0]}-01`
      )
    : "";
  const [allReportData, setAllReportData] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState();
  const [sortOrder, setSortOrder] = useState();
  const [page, setPage] = useState(defaultPage || 1);
  const [limit, setLimit] = useState(defaultLimit || defaultLimitPagination);
  const [total, setTotal] = useState();
  const [from, setFrom] = useState();
  const [to, setTo] = useState();
  const { trustId: trustID } = useTrust(org);
  const [trustId, setTrustId] = useState(org ? "" : trustID);
  const [trustListData, setTrustListData] = useState([]);

  const [selectedMonthRange, setSelectedMonthRange] = React.useState(
    fromMonth && toMonth
      ? {
          start: fromMonth,
          end: toMonth,
        }
      : {
          start: pastFinancialdate[0],
          end: pastFinancialdate[1],
        }
  );

  const reportFilterList = [
    {
      label: "Both",
      value: "1",
    },
    {
      label: "Only Credit",
      value: "2",
    },
    {
      label: "Only Debit",
      value: "3",
    },
  ];

  const [vendorId, setVendorId] = useState();
  const [contactId, setContactId] = useState();
  const [reversedId, setReversedId] = useState();
  const [reportFilterValue, setReportFilterValue] = useState("1");

  const [activeTrustId, setActiveTrustId] = React.useState(null);
  const { organizationData } = useOrganization();

  useEffect(() => {
    setTrustId(trustID);
  }, [trustID]);

  const fetchTustCashbookReportData = useCallback(async () => {
    try {
      setLoading(true);
      const formData = new FormData();
      if (trustId?.length > 0) {
        trustId?.map((item, index) =>
          formData.append(`trust_ids[${index}]`, item)
        );
      }
      formData.append("type", "transaction-report");

      formData.append(
        "organization_id",
        organizationData?.organizationId ? organizationData?.organizationId : ""
      );
      formData.append(
        `from`,
        moment(selectedMonthRange?.start).format("MM-YYYY")
          ? moment(selectedMonthRange?.start).format("MM-YYYY")
          : ""
      );
      formData.append(
        `to`,
        moment(selectedMonthRange?.end).format("MM-YYYY")
          ? moment(selectedMonthRange?.end).format("MM-YYYY")
          : ""
      );

      const { data } = await fileUpload(
        `${API.ORG_GENERAL_LEDGER_REPORT}`,
        formData
      );
      setTrustListData(data?.data);
      let activetTust = data?.data?.filter((item) => item?.transactions === 1);

      if (activetTust?.[0]?.id !== undefined) {
        setActiveTrustId(activetTust?.[0]?.id);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setReportData([]);
    }
  }, [
    trustId,
    organizationData?.organizationId,
    selectedMonthRange?.start,
    selectedMonthRange?.end,
  ]);

  useEffect(() => {
    if (org) {
      fetchTustCashbookReportData();
    }
  }, [org, fetchTustCashbookReportData]);

  const handleAccordionChange = async (value) => {
    if (!value) {
      setActiveTrustId(null);
      return;
    }

    setActiveTrustId(value);
  };

  const fetchTransactionsReportData = useCallback(
    async (limit, page, activeTrustId) => {
      setAllReportData();
      setReportData();
      try {
        setLoading(true);
        const formData = new FormData();

        formData.append(
          "trust_id",
          org && activeTrustId ? activeTrustId : trustId ? trustId : ""
        );
        if (vendorId?.length > 0) {
          vendorId?.map((item, index) =>
            formData.append(`vendor_ids[${index}]`, item)
          );
        }

        if (contactId?.length > 0) {
          contactId?.map((item, index) =>
            formData.append(`contact_ids[${index}]`, item)
          );
        }

        formData.append("is_reversed", reversedId ? reversedId : "0");
        formData.append(
          "transaction_type",
          reportFilterValue ? reportFilterValue : "1"
        );

        formData.append(
          "from",
          moment(selectedMonthRange?.start).format("MM-YYYY")
            ? moment(selectedMonthRange?.start).format("MM-YYYY")
            : ""
        );
        formData.append(
          "to",
          moment(selectedMonthRange?.end).format("MM-YYYY")
            ? moment(selectedMonthRange?.end).format("MM-YYYY")
            : ""
        );
        // formData.append("export", 0);
        formData.append("limit", limit ? limit : defaultLimitPagination);
        formData.append("page", page ? page : 1);
        const { data } = await fileUpload(
          `${API.TRANSACTION_REPORT}`,
          formData
        );

        setLoading(false);
        setLimit(data?.data?.[0]?.reports?.per_page);
        setPage(data?.data?.[0]?.reports?.current_page);
        setTotal(data?.data?.[0]?.reports?.total);
        setFrom(data?.data?.[0]?.reports?.from);
        setTo(data?.data?.[0]?.reports?.to);
        setAllReportData(data?.data?.[0]);
        const reportData = data?.data?.[0]?.reports?.data
          ? Object.values(data?.data?.[0]?.reports?.data)
          : [];

        setReportData(reportData);
      } catch (error) {
        setLoading(false);
        setReportData([]);
      }
    },
    [
      org,
      trustId,
      vendorId,
      contactId,
      selectedMonthRange?.start,
      selectedMonthRange?.end,
      reversedId,
      reportFilterValue,
    ]
  );

  useEffect(() => {
    if (org) {
      if (activeTrustId) {
        fetchTransactionsReportData(defaultLimitPagination, 1, activeTrustId);
      }
    } else if (!org) {
      fetchTransactionsReportData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTrustId, fetchTransactionsReportData, org]);

  /**
   * Vendors Limit Per Page
   */
  const handleUpdateLimit = (e) => {
    const limit = e.target.value;
    fetchTransactionsReportData(limit, 1, activeTrustId);
  };

  /**
   * Pagination
   */
  const handleChangePage = (page) => {
    fetchTransactionsReportData(limit, page, activeTrustId);
  };

  /**
   * Table Change For Sorting
   */
  const handleTableChange = (type, { page, sortField, sortOrder }) => {
    if (type === "sort") {
      setPage(1);
      setSortField(sortField);
      setSortOrder(
        sortOrder === "asc" ? "ASC" : sortOrder === "desc" && "DESC"
      );
    }
  };

  useEffect(() => {
    if (!org) {
      navigate(
        `?limit=${limit ? limit : defaultLimitPagination}&page=${
          page ? page : 1
        }&sort_column=${sortField ? sortField : ""}&sort_order=${
          sortOrder ? sortOrder : ""
        }&from=${
          moment(selectedMonthRange?.start).format("MM-YYYY")
            ? moment(selectedMonthRange?.start).format("MM-YYYY")
            : ""
        }&to=${
          moment(selectedMonthRange?.end).format("MM-YYYY")
            ? moment(selectedMonthRange?.end).format("MM-YYYY")
            : ""
        }`,
        {
          replace: true,
        }
      );
    }
  }, [
    navigate,
    limit,
    page,
    sortField,
    sortOrder,
    selectedMonthRange?.start,
    selectedMonthRange?.end,
    org,
  ]);

  const reportGenerator = (quantity) => {
    if (quantity > 0) {
      const items = [];
      items.push({
        key: "opening-balance",
        date: allReportData?.opening_balance_date ? (
          <b>
            {moment(allReportData?.opening_balance_date, "YYYY-MM-DD").format(
              "DD-MM-YYYY"
            )}
          </b>
        ) : (
          ""
        ),
        transaction_no: <b>Opening Balance</b>,
        reason: "",
        debit: "",
        credit: "",
        deposit_withdrawal: "",
        balance: allReportData?.opening_balance ? (
          <b>
            {NegativeAmountConverter(Number(allReportData?.opening_balance))}
          </b>
        ) : (
          <b>$0.00</b>
        ),
      });
      for (let i = 0; i < quantity; i++) {
        for (let j = 0; j < reportData[i]?.payment_data?.length; j++) {
          items.push({
            key: i,
            date:
              j === 0 && reportData[i]?.transaction_date ? (
                moment(reportData[i]?.transaction_date, "YYYY-MM-DD").format(
                  "DD-MM-YYYY"
                )
              ) : (
                <div className="blank-td" />
              ),
            transaction_no: reportData[i]?.transaction_id
              ? reportData[i]?.transaction_id
              : "",
            is_reversed: reportData[i]?.is_reversed
              ? reportData[i]?.is_reversed
              : "",
            reason: reportData[i]?.payment_data[j]?.description
              ? reportData[i]?.payment_data[j]?.description
              : "",
            debit:
              reportData[i]?.transaction_type == 0 ? (
                <span
                  className={
                    reportData[i]?.transaction_type
                      ? "text-green-500"
                      : "text-red-500"
                  }
                >
                  {reportData[i]?.payment_data[j]?.amount
                    ? `$${formatNumber(
                        Number(reportData[i]?.payment_data[j]?.amount)
                      )}`
                    : "$0.00"}
                </span>
              ) : (
                <>-</>
              ),
            credit:
              reportData[i]?.transaction_type == 1 ? (
                <span
                  className={
                    reportData[i]?.transaction_type
                      ? "text-green-500"
                      : "text-red-500"
                  }
                >
                  {reportData[i]?.payment_data[j]?.amount
                    ? `$${formatNumber(
                        Number(reportData[i]?.payment_data[j]?.amount)
                      )}`
                    : "$0.00"}
                </span>
              ) : (
                <>-</>
              ),
            deposit_withdrawal: (
              <div className="align-top">
                {formatContactName(
                  reportData[i]?.payment_data[j]?.type,
                  {
                    last_name: reportData[i]?.payment_data[j]?.contact_name,
                  },
                  organizationData?.organizationData
                )}
                {reportData[i]?.transaction_type === 0 && (
                  <>
                    <br />
                    <strong>Account Name: </strong>
                    {reportData[i]?.payment_data[j]?.account_name
                      ? reportData[i]?.payment_data[j]?.account_name
                      : ""}
                    <br />
                    <strong>BSB: </strong>
                    {reportData[i]?.payment_data[j]?.bsb
                      ? formatBSBNumber(reportData[i]?.payment_data[j]?.bsb)
                      : ""}
                    <br />
                    <strong>Account Number: </strong>
                    {reportData[i]?.payment_data[j]?.account_number
                      ? formatAccountNumber(
                          reportData[i]?.payment_data[j]?.account_number
                        )
                      : ""}
                  </>
                )}
              </div>
            ),
            balance:
              j === 0 ? (
                reportData[i]?.closing_balance ? (
                  NegativeAmountConverter(
                    Number(reportData[i]?.closing_balance)
                  )
                ) : (
                  "$0.00"
                )
              ) : (
                <div className="blank-td" />
              ),
          });
        }
      }
      items.push({
        key: "closing-balance",
        date: allReportData?.filter?.end_month ? (
          <b>
            {moment(allReportData?.filter?.end_month, "YYYY-MM-DD").format(
              "DD-MM-YYYY"
            )}
          </b>
        ) : (
          ""
        ),
        transaction_no: <b>Closing Balance</b>,
        reason: "",
        debit: "",
        credit: "",
        deposit_withdrawal: "",
        balance: allReportData?.closing_balance ? (
          <b>
            {NegativeAmountConverter(Number(allReportData?.closing_balance))}
          </b>
        ) : (
          <b>$0.00</b>
        ),
      });
      return items;
    }
  };

  const reportDatas = reportGenerator(reportData?.length);

  /**
   * Table Heading Data
   */
  const columns = [
    {
      dataField: "date",
      text: "Date",
      headerClasses: THClasses,
      classes: (cell, row) => {
        if (row?.key === "opening-balance") {
          return `${TDClasses} border-r-0`;
        }
        if (row?.key === "closing-balance") {
          return `${TDClasses} border-r-0`;
        }
        return TDClasses;
      },
    },
    {
      dataField: "transaction_no",
      text: "Trans No.",
      headerClasses: THClasses,
      classes: (cell, row) => {
        if (row?.key === "opening-balance") {
          return `${TDClasses} border-r-0`;
        }
        if (row?.key === "closing-balance") {
          return `${TDClasses} border-r-0`;
        }
        return TDClasses;
      },
    },
    {
      dataField: "reason",
      text: "Reason",
      headerClasses: THClasses,
      classes: (cell, row) => {
        if (row?.key === "opening-balance") {
          return `${TDClasses} border-r-0`;
        }
        if (row?.key === "closing-balance") {
          return `${TDClasses} border-r-0`;
        }
        return TDClasses;
      },
    },
    {
      dataField: "debit",
      text: "Debit",
      headerClasses: THClasses,
      classes: (cell, row) => {
        if (row?.key === "opening-balance") {
          return `${TDClasses} border-r-0`;
        }
        if (row?.key === "closing-balance") {
          return `${TDClasses} border-r-0`;
        }
        return TDClasses;
      },
    },
    {
      dataField: "credit",
      text: "Credit",
      headerClasses: THClasses,
      classes: (cell, row) => {
        if (row?.key === "opening-balance") {
          return `${TDClasses} border-r-0`;
        }
        if (row?.key === "closing-balance") {
          return `${TDClasses} border-r-0`;
        }
        return TDClasses;
      },
    },
    {
      dataField: "deposit_withdrawal",
      text: "Deposit from / Withdrawal for",
      headerClasses: THClasses,
      classes: (cell, row) => {
        if (row?.key === "opening-balance") {
          return `${TDClasses} border-r-0`;
        }
        if (row?.key === "closing-balance") {
          return `${TDClasses} border-r-0`;
        }
        return TDClasses;
      },
    },
    {
      dataField: "balance",
      text: "Balance",
      headerClasses: THClasses,
      classes: TDClasses,
    },
  ];

  const handleExport = async (format) => {
    try {
      setLoading(true);
      const formData = new FormData();
      if (org) {
        if (trustId?.length > 0) {
          trustId?.map((item, index) =>
            formData.append(`trust_ids[${index}]`, item)
          );
        }

        formData.append(
          "organization_id",
          organizationData?.organizationId
            ? organizationData?.organizationId
            : ""
        );
      } else if (!org) {
        formData.append(`trust_id`, trustId ? trustId : "");
      }

      formData.append("is_reversed", reversedId ? reversedId : "0");
      formData.append(
        "transaction_type",
        reportFilterValue ? reportFilterValue : "1"
      );

      if (vendorId?.length > 0) {
        vendorId?.map((item, index) =>
          formData.append(`vendor_ids[${index}]`, item)
        );
      }

      if (contactId?.length > 0) {
        contactId?.map((item, index) =>
          formData.append(`contact_ids[${index}]`, item)
        );
      }

      formData.append("format", format);
      formData.append(
        "from",
        moment(selectedMonthRange?.start).format("MM-YYYY")
          ? moment(selectedMonthRange?.start).format("MM-YYYY")
          : ""
      );
      formData.append(
        "to",
        moment(selectedMonthRange?.end).format("MM-YYYY")
          ? moment(selectedMonthRange?.end).format("MM-YYYY")
          : ""
      );
      formData.append("export", 1);
      formData.append("limit", limit ? limit : defaultLimitPagination);
      formData.append("page", page ? page : 1);
      formData.append("format", format);
      const { data } = await fileUpload(`${API.TRANSACTION_REPORT}`, formData);
      const export_report_path = data?.data?.file;
      if (export_report_path) {
        var a = document.createElement("a");
        a.href = export_report_path;
        var file = export_report_path.split("/");
        a.setAttribute(
          "download",
          file[file.length - 1] || "transactionsreport.pdf"
        );
        a.setAttribute("target", "_blank");
        document.body.appendChild(a); // we need to append the element to the dom -> otherwise it will not work in firefox
        a.click();
        a.remove();
        setLoading(false);
      }
    } catch (e) {
      setLoading(false);
    }
  };

  // const [columns, setColumns] = useState(columnsData);

  // useEffect(() => {
  //   if (reportFilterValue === "3") {
  //     setColumns(columnsData.filter((item) => item.dataField !== "credit"));
  //   } else if (reportFilterValue === "2") {
  //     setColumns(columnsData.filter((item) => item.dataField !== "debit"));
  //   } else {
  //     setColumns(columnsData);
  //   }
  // }, [columnsData, reportFilterValue]);

  return (
    <>
      <MainBody
        search={false}
        actionRight={isDesktop ? true : false}
        actionContent={
          <>
            <MonthRangePicker
              onMonthRangeSelect={setSelectedMonthRange}
              selectedMonthRange={selectedMonthRange}
              variant="outline"
              align={isDesktop ? "end" : "start"}
              maxDate={new Date()}
            />

            <CommonFilters
              trust={org}
              trustId={trustId}
              multiTrust={org}
              setTrustId={setTrustId}
              vendor={true}
              setVendorId={setVendorId}
              vendorId={vendorId}
              multiVendor={true}
              contact={true}
              setContactId={setContactId}
              contactId={contactId}
              multiContact={true}
              reportFilterList={reportFilterList}
              reportFilter={true}
              reportFilterValue={reportFilterValue}
              setReportFilterValue={setReportFilterValue}
              reversed={true}
              reversedId={reversedId}
              setReversedId={setReversedId}
            />

            <DropDownButton
              items={[
                {
                  label: "PDF",
                  onClick: () => handleExport("pdf"),
                  disabled: loading,
                },
                {
                  label: "Excel",
                  onClick: () => handleExport("xlsx"),
                  disabled: loading,
                },
              ]}
              text={
                <div className="flex gap-2 items-center">
                  Export <Download size={18} />
                </div>
              }
            />
          </>
        }
        loading={loading}
      >
        {org && trustListData?.length > 0 ? (
          <>
            <Accordion
              type="single"
              collapsible
              value={activeTrustId}
              onValueChange={handleAccordionChange}
              className="w-full"
            >
              {trustListData.map((item) => {
                const trustLabel = item?.business_name
                  ? item?.business_name
                  : item?.first_name
                  ? item?.first_name + " " + item?.last_name
                  : item?.last_name;

                return (
                  <AccordionItem
                    key={item?.id}
                    value={item?.id}
                    className="border-none mb-2"
                  >
                    <AccordionTrigger className="text-base font-medium bg-webSecondary hover:bg-webSecondary text-white dark:bg-muted px-4 py-2 rounded-md dark:hover:bg-muted/70">
                      <div className="flex items-center justify-between w-full pr-3">
                        {trustLabel}
                        <div className="flex gap-3">
                          <div>
                            Opening Balance:{" "}
                            <span>
                              {item?.opening_balance
                                ? item?.opening_balance > 0
                                  ? `($${formatNumber(
                                      Number(item?.opening_balance)
                                    )})`
                                  : `$${formatNumber(
                                      Math.abs(Number(item?.opening_balance))
                                    )}`
                                : "$0.00"}
                            </span>
                          </div>
                          <div>
                            Closing Balance:{" "}
                            <span>
                              {item?.closing_balance
                                ? item?.closing_balance > 0
                                  ? `($${formatNumber(
                                      Number(item?.closing_balance)
                                    )})`
                                  : `$${formatNumber(
                                      Math.abs(Number(item?.closing_balance))
                                    )}`
                                : "$0.00"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="custom-table mt-2">
                        <div
                          className={
                            "table-body-class sm:max-h-[600px] rounded-md"
                          }
                        >
                          <BootstrapTable
                            keyField="key"
                            remote
                            data={
                              limit && page && reportData?.length > 0
                                ? reportDatas
                                : []
                            }
                            columns={reportFilterValue === "3" ? columns.filter((item) => item.dataField !== "credit") : reportFilterValue === "2" ? columns.filter((item) => item.dataField !== "debit") : columns}
                            onTableChange={handleTableChange}
                            rowClasses={(row) => {
                              if (row?.is_reversed) {
                                return `${TRClasses} bg-muted dark:text-gray-400 text-gray-600 dark:bg-muted/50`;
                              }
                              if (row?.key === "opening-balance") {
                                return `${TRClasses} dark:bg-muted bg-webSecondary dark:hover:!bg-muted hover:!bg-webSecondary dark:text-foreground text-white sticky top-8`;
                              }
                              if (row?.key === "closing-balance") {
                                return `${TRClasses} dark:bg-muted bg-webSecondary dark:hover:!bg-muted hover:!bg-webSecondary dark:text-foreground text-white sticky bottom-0`;
                              }
                              return TRClasses;
                            }}
                            noDataIndication="No Data Found"
                            classes={TableClasses}
                            wrapperClasses={TableWrapClasses}
                          />
                        </div>

                        <Pagination
                          total={total}
                          limit={parseInt(limit)}
                          currentPage={page}
                          updateLimit={handleUpdateLimit}
                          updatePage={handleChangePage}
                          from={from}
                          to={to}
                        />
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </>
        ) : !org ? (
          <div className="custom-table">
            <div className={"table-body-class"}>
              <BootstrapTable
                keyField="key"
                remote
                data={
                  limit && page && reportData?.length > 0 ? reportDatas : []
                }
                // columns={columns}
                            columns={reportFilterValue === "3" ? columns.filter((item) => item.dataField !== "credit") : reportFilterValue === "2" ? columns.filter((item) => item.dataField !== "debit") : columns}

                onTableChange={handleTableChange}
                rowClasses={(row) => {
                  if (row?.key === "opening-balance") {
                    return `${TRClasses} dark:bg-muted bg-webSecondary dark:hover:!bg-muted hover:!bg-webSecondary dark:text-foreground text-white sticky top-8`;
                  }
                  if (row?.key === "closing-balance") {
                    return `${TRClasses} dark:bg-muted bg-webSecondary dark:hover:!bg-muted hover:!bg-webSecondary dark:text-foreground text-white sticky bottom-0`;
                  }

                  if (row?.is_reversed) {
                    return `${TRClasses} bg-muted dark:text-gray-400 text-gray-600 dark:bg-muted/50`;
                  }
                  return TRClasses;
                }}
                noDataIndication="No Data Found"
                classes={TableClasses}
                wrapperClasses={TableWrapClasses}
              />
            </div>

            <Pagination
              total={total}
              limit={parseInt(limit)}
              currentPage={page}
              updateLimit={(e) => handleUpdateLimit(e)}
              updatePage={(page) => handleChangePage(page)}
              from={from}
              to={to}
            />
          </div>
        ) : (
          <>
            <div className="flex h-full items-center justify-center p-4">
              <Card className="max-w-md w-full shadow-lg text-center">
                <CardContent className="flex flex-col items-center gap-4 py-10">
                  <Home className="h-12 w-12 text-webPrimary" />
                  <h2 className="text-2xl font-semibold ">
                    Please Select Trust{" "}
                  </h2>
                  <p>To get Report Data.</p>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </MainBody>
    </>
  );
};

export default Cashbook;
